"use client";

import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { Banner } from "@/components/Banner";
import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { Switch } from "@/components/shadcn/switch";
import { BindableInput } from "./BindableInput";
import { cn } from "@/lib/utils";

/**
 * V2 renderer for `display=ConfigBindableBool`. Literal mode is a switch;
 * variable mode shows a chip when the stored value is exactly `:var[<ref>]`. The picker filters to bool-typed sources. Shares the bind
 * affordance + chip semantics with the other bindable fields via
 * {@link BindableInput} (bool `dataType`).
 */
export function BindableBoolField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  disabled,
  placeholder,
  isRequired,
  showOptionalLabel,
  error,
  defaultValue,
  widget = "switch",
}: {
  nodeId: string;
  fieldKey: string;
  label: string;
  tooltip?: string;
  description?: string;
  disabled?: boolean;
  placeholder?: string;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  error?: string;
  defaultValue?: boolean;
  widget?: "switch" | "select";
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path);
  const value: string =
    typeof raw === "string" ? raw : typeof raw === "boolean" ? String(raw) : "";
  const resolvedLabel = useMemo(
    () => label || humanize(fieldKey),
    [label, fieldKey]
  );
  const invalid = !!error;
  // A cleared variable stores the default: the backend rejects "" for a bool.
  const setValue = (next: string | boolean) =>
    form.setValue(path, next === "" ? defaultValue === true : next, {
      shouldDirty: true,
      shouldTouch: true,
    });
  const checked = value === "" ? defaultValue === true : value === "true";

  return (
    <div className="flex flex-col gap-1">
      <NoxusFormLabel
        label={resolvedLabel}
        tooltip={tooltip}
        name={path}
        noMargin
        isDisabled={disabled}
        isRequired={isRequired}
        showOptionalLabel={showOptionalLabel}
      />
      <div
        className={cn(
          invalid && "rounded-md ring-1 ring-inset ring-amber-400/70"
        )}
      >
        <BindableInput
          nodeId={nodeId}
          dataType="bool"
          inputName={fieldKey}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={setValue}
          renderLiteral={
            widget === "select" ? undefined : (
              <div className="flex h-8 items-center">
                <Switch
                  id={path}
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={setValue}
                />
              </div>
            )
          }
        />
      </div>
      {description && !error ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      {error ? <Banner label={error} style="warning" size="sm" /> : null}
    </div>
  );
}

function humanize(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}
